import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import {
  Server, Database, HardDrive, Cpu, Clock, Shield,
  CheckCircle, XCircle, RefreshCw,
} from 'lucide-react';

interface HealthData {
  server: { status: string; uptime: string };
  database: { sizeMB: number; totalTables: number; totalRowsEstimate: number };
  redis: { status: string; memoryMB: number };
  disk: { usedPercent: number; totalGB: number };
  pm2: { processes: number; status: string };
  lastBackup: string | null;
  ssl: { expiresAt: string };
}

function StatusBadge({ status }: { status: string }) {
  const isOnline = status === 'online' || status === 'connected';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
      isOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
    }`}>
      {isOnline ? <CheckCircle size={12} /> : <XCircle size={12} />}
      {status}
    </span>
  );
}

export function SystemHealthPage() {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/admin/health')
      .then(res => setData(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center text-gray-500">Failed to load health data.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Health</h1>
          <p className="text-sm text-gray-500">Infrastructure and service status</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Server Status */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <Server size={20} className="text-blue-500" />
            <h3 className="text-sm font-medium text-gray-700">Server</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Status</span>
              <StatusBadge status={data.server.status} />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Uptime</span>
              <span className="font-medium text-gray-900">{data.server.uptime}</span>
            </div>
          </div>
        </div>

        {/* Database */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <Database size={20} className="text-violet-500" />
            <h3 className="text-sm font-medium text-gray-700">PostgreSQL</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Size</span>
              <span className="font-medium text-gray-900">{data.database.sizeMB} MB</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Tables</span>
              <span className="font-medium text-gray-900">{data.database.totalTables}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Rows (est.)</span>
              <span className="font-medium text-gray-900">{data.database.totalRowsEstimate.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Redis */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <Cpu size={20} className="text-red-500" />
            <h3 className="text-sm font-medium text-gray-700">Redis</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Status</span>
              <StatusBadge status={data.redis.status} />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Memory</span>
              <span className="font-medium text-gray-900">
                {data.redis.memoryMB > 0 ? `${data.redis.memoryMB} MB` : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Disk */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <HardDrive size={20} className="text-amber-500" />
            <h3 className="text-sm font-medium text-gray-700">Disk</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Usage</span>
              <span className="font-medium text-gray-900">
                {data.disk.usedPercent > 0 ? `${data.disk.usedPercent}%` : 'Configure monitoring'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total</span>
              <span className="font-medium text-gray-900">
                {data.disk.totalGB > 0 ? `${data.disk.totalGB} GB` : '--'}
              </span>
            </div>
          </div>
        </div>

        {/* PM2 */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <Cpu size={20} className="text-blue-500" />
            <h3 className="text-sm font-medium text-gray-700">PM2 Processes</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Status</span>
              <StatusBadge status={data.pm2.status} />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Processes</span>
              <span className="font-medium text-gray-900">{data.pm2.processes}</span>
            </div>
          </div>
        </div>

        {/* Backup & SSL */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <Shield size={20} className="text-emerald-500" />
            <h3 className="text-sm font-medium text-gray-700">Backup & SSL</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Last Backup</span>
              <span className="font-medium text-gray-900">
                {data.lastBackup || (
                  <span className="text-amber-600">Not configured</span>
                )}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">SSL Expiry</span>
              <span className="font-medium text-gray-900">
                {new Date(data.ssl.expiresAt).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
